import { Controller } from "@hotwired/stimulus";

/**
 * Search as you type inside a bottom sheet, with the results as a Turbo frame.
 *
 * The three sheets that pick something out of a search share this: the doubles partner on an
 * enrollment, the home club and the player references on a registration. What they share is
 * everything except what choosing a row means, which is `onPick` and is left to the subclass.
 *
 * It is deliberately not the autocomplete controller. That one is a combobox: a floating list under
 * a field, which blur closes and Escape dismisses. Here the list is the sheet's body, so closing it
 * on blur empties the sheet when somebody taps its header, and swallowing Escape stops the key that
 * is supposed to dismiss the sheet. Both are right for a dropdown and wrong for this.
 *
 * What it does instead is what `search-controller` does, because this is the same screen in a sheet:
 * wait out the typing, then point a Turbo frame at the address the results live at. The frame
 * renders rows, the invitation or the dead end, so this file never builds markup and a sheet never
 * has a second opinion about what a result looks like.
 *
 * Opening and dismissing the sheet are not here at all. `data-wp-sheet-open` is the component
 * library's, and the <dialog> it opens brings the backdrop, the focus trap, Escape and
 * pull-to-dismiss with it.
 */

/* iOS's numbers, from `SearchViewModel`, which the search screen holds to as well: 500ms after the
   typing stops, and nothing is asked for under three characters. The server applies the same floor
   -- the address is a URL anybody can request -- so this is the keystroke saved, not the rule. */
const DEBOUNCE_MS = 500;
const MINIMUM_LENGTH = 3;
const PARAM_QUERY = "query";

export default abstract class SheetSearchController extends Controller<HTMLDialogElement> {
  static override targets = ["frame"];

  static override values = {
    url: String,
  };

  declare readonly frameTarget: HTMLElement;
  declare readonly urlValue: string;

  private pending: ReturnType<typeof setTimeout> | undefined;

  /** The address the frame already shows, so typing and deleting a character asks for nothing. */
  private loaded = "";

  /**
   * What choosing a row means. Bound on the frame, which Turbo replaces on every search and which
   * therefore cannot carry a listener of its own.
   */
  abstract onPick(event: Event): void;

  /*
   * The frame arrives server-rendered with the invitation in it, which is the answer to an empty
   * query. Saying so here rather than leaving this empty is what stops the first keystroke under
   * the minimum fetching that same invitation again.
   */
  override connect(): void {
    this.loaded = this.address();
  }

  override disconnect(): void {
    clearTimeout(this.pending);
  }

  /*
   * The busy flag goes on at the keystroke rather than when the request starts, as it does on the
   * search screen and for the same reason: for the next half second the list is an answer to a
   * question that is no longer being asked, and nothing would say so. Turbo marks and clears the
   * frame busy around its own fetch, so it takes the flag over from here.
   */
  onInput(): void {
    clearTimeout(this.pending);
    this.frameTarget.setAttribute("aria-busy", "true");
    this.pending = setTimeout(() => this.search(), DEBOUNCE_MS);
  }

  /**
   * Enter searches now, and never submits.
   *
   * A sheet renders inside whatever form it was opened over - a <dialog> moves to the top layer to
   * be drawn but stays where it is in the document - and there is no inner form to catch the key,
   * because a form inside a form is not a form. Without this, pressing Enter on a half-typed name
   * submits the step.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    clearTimeout(this.pending);
    this.search();
  }

  /**
   * Back to the empty prompt: the query cleared and the frame pointed at the invitation again.
   *
   * For a sheet that is reopened later. Leaving the last search in it means reopening onto results
   * for something somebody has already finished looking for, which is what `resetPlayerSearchResults`
   * exists for on iOS.
   */
  protected reset(): void {
    clearTimeout(this.pending);
    this.field().value = "";
    this.search();
  }

  /*
   * The field is found rather than declared as a target, as the search screen's is: it is drawn by
   * the component library's `search` fragment, which takes the handful of parameters it takes and
   * knows nothing about Stimulus.
   */
  protected field(): HTMLInputElement {
    const field = this.element.querySelector<HTMLInputElement>(
      'input[type="search"]',
    );
    if (field === null) {
      throw new Error("this sheet has no search field in it");
    }

    return field;
  }

  private search(): void {
    const address = this.address();

    /* Nothing to ask for: the query came back to what the frame already shows, which is what
       deleting the character you just typed does. Turbo never starts, so the flag it would have
       cleared has to come off here. */
    if (address === this.loaded) {
      this.frameTarget.removeAttribute("aria-busy");

      return;
    }

    this.loaded = address;
    this.frameTarget.setAttribute("src", address);
  }

  /**
   * Where the results for whatever is in the field right now live.
   *
   * Under the minimum the query is left off altogether, which asks for the invitation: the same
   * thing a query that short does on iOS, where it clears the results and hands the screen back.
   * No history is touched, unlike the search screen's own: a sheet over a form is not an address
   * anybody navigated to, and replacing the URL from inside one would make the back button leave
   * the form.
   */
  private address(): string {
    const url = new URL(this.urlValue, window.location.href);
    const query = this.field().value.trim();

    if (query.length >= MINIMUM_LENGTH) {
      url.searchParams.set(PARAM_QUERY, query);
    } else {
      url.searchParams.delete(PARAM_QUERY);
    }

    return `${url.pathname}${url.search}`;
  }
}
